'use client';

import { c as _c } from "react/compiler-runtime";
import { Fragment, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, ChevronRight, Clock3, Compass, Lock, Sparkles, Users } from 'lucide-react';
import { useLabsStore, usePack } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { BrandButton, MetaTag, PillTag } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/_components';
import { StepShell } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/step-shell';
import { StepCard } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/welcome';

/**
 * The public preview's front door.
 *
 * 🔴 WHY THIS IS NOT THE SPRINT'S WELCOME SCREEN. That screen makes three promises the preview does
 * not keep, and all three live in the pack's shared `copy.welcome`:
 *
 *   1. "Spend 15 minutes finding out." The preview is about five.
 *   2. "About 15 minutes" on the meta row. Same claim, second place.
 *   3. Step 3, "Analyze a real company / product / business". The preview drops M3 and M4, so it never
 *      analyses anything.
 *
 * Reusing it meant opening by advertising the product this is a cut of, which is the one thing a
 * five-minute funnel cannot afford. The reader who came for a fifteen-minute analysis is exactly the
 * reader who leaves disappointed at minute four.
 *
 * WHAT COMES FROM THE PACK, AND WHAT DOES NOT. The vertical still names itself (`eyebrow`) and still
 * asks its own question (`headlineLead`), because those are the domain and they are as true here as in
 * the sprint. Everything about length and shape belongs to the surface, not the vertical, so it lives
 * here and reads identically across all three packs. The first two step cards are reused word for
 * word, since they describe the two modules the preview actually runs. Only the third is replaced.
 *
 * If a vertical ever needs its own preview wording, that is the moment to add a `previewWelcome` block
 * to `LabsCopy`, and not before. Three identical strings do not need three authored copies.
 */

/** Icons for the three cards, zipped by index. Card 3 is the result here, not an analysis. */
const STEP_ICONS: readonly LucideIcon[] = [Users, Compass, Sparkles];

/** The surface's own length claim. Measured: welcome to the archetype reveal is 202s, plus the payoff. */
const PREVIEW_HEADLINE_TAIL = 'Spend 5 minutes finding out.';
const PREVIEW_META_DURATION = 'About 5 minutes';

/** Names the pill for what this is. The sprint's own label is "Discovery experience". */
const PREVIEW_PILL_SUFFIX = '· 5-minute preview';

/**
 * True for every vertical: the axis always contrasts two poles, and the preview always ends on the
 * archetype rather than on an artifact.
 */
const PREVIEW_LEAD = 'Two real career paths. See which one is you.';

/** What the preview actually ends on, replacing the sprint's "Analyze a real ..." card. */
const PREVIEW_FINAL_STEP = {
  sub: 'Plus the externships that fit how you think',
  title: 'Get your result'
};
export function PreviewWelcome() {
  const $ = _c(22);
  const {
    copy
  } = usePack();
  const next = useLabsStore(_temp);
  let t0;
  if ($[0] !== copy.welcome.steps) {
    t0 = [...copy.welcome.steps.slice(0, 2), PREVIEW_FINAL_STEP];
    $[0] = copy.welcome.steps;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const steps = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="opacity-70">{PREVIEW_PILL_SUFFIX}</span>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== copy.welcome.eyebrow) {
    t2 = <PillTag>{copy.welcome.eyebrow} {t1}</PillTag>;
    $[3] = copy.welcome.eyebrow;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-fs-text-subtle">{PREVIEW_HEADLINE_TAIL}</span>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== copy.welcome.headlineLead) {
    t4 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{copy.welcome.headlineLead} {t3}</h1>;
    $[6] = copy.welcome.headlineLead;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="font-medium text-fs-text-subtle text-fs-title-lg tracking-[-1px]">{PREVIEW_LEAD}</p>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <div className="flex flex-col items-center gap-2 sm:gap-4">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== steps) {
    t7 = <ol className="flex w-full flex-col items-stretch gap-2 text-left sm:flex-row sm:items-stretch sm:gap-0">{steps.map((step, i) => <Fragment key={step.title}><li className="flex sm:flex-1"><StepCard icon={STEP_ICONS[i] ?? Users} n={i + 1} sub={step.sub} title={step.title} /></li>{i < steps.length - 1 ? <li aria-hidden={true} className="hidden items-center justify-center px-2 text-fs-icon-subtle sm:flex"><ChevronRight className="h-4 w-4" strokeWidth={2} /></li> : null}</Fragment>)}</ol>;
    $[11] = steps;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-4"><MetaTag icon={Clock3}>{PREVIEW_META_DURATION}</MetaTag><MetaTag icon={Lock}>No account needed</MetaTag></div>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== copy.welcome.cta || $[15] !== next) {
    t9 = <BrandButton icon={ArrowRight} onClick={next}>{copy.welcome.cta}</BrandButton>;
    $[14] = copy.welcome.cta;
    $[15] = next;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t2 || $[18] !== t6 || $[19] !== t7 || $[20] !== t9) {
    t10 = <StepShell showProgress={false}><div className="flex flex-col items-center gap-4 text-center sm:gap-8">{t2}{t6}{t7}{t8}{t9}</div></StepShell>;
    $[17] = t2;
    $[18] = t6;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function _temp(s) {
  return s.next;
}